import { useInfiniteQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface TicketSummary {
  ticketId: string;
  subject: string;
  status: string;
  categoryId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface UseTicketsOptions {
  status?: string;
  categoryId?: string;
}

interface UseTicketsReturn {
  tickets: TicketSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

const LIMIT = 20;

export function useTickets(options: UseTicketsOptions = {}): UseTicketsReturn {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: queryKeys.customerSupport.ticketList({
      status: options.status,
      categoryId: options.categoryId,
    }),
    queryFn: async ({ pageParam = 0 }) => {
      return api.listSupportTickets({
        status: options.status || undefined,
        categoryId: options.categoryId || undefined,
        limit: LIMIT,
        offset: pageParam,
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.tickets.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });

  const tickets = data?.pages.flatMap((p) => p.tickets) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  return {
    tickets,
    total,
    loading: isLoading,
    error: error ? error.message : null,
    refresh: () => refetch(),
    loadMore: () => fetchNextPage(),
    hasMore: !!hasNextPage,
  };
}
